import React, { useCallback, useEffect, useState } from 'react';
import { useLiveRefresh } from '../../../context/LiveUpdatesContext';
import { toast } from 'react-hot-toast';
import { CalendarCheck, Timer, Download } from 'lucide-react';
import { DataTable, Person, Button, Select } from '../../../components';
import { homeAgencyService } from '../../../services/agencyService';
import AttendanceModal from './AttendanceModal';
import { Stat } from '../../agency/AgencyHours';
import {
  CARD, inputClass, FieldLabel, FilterTabs, BookingPill, HoursPill, errorMessage, dayLabel, timeRange, clockOf,
  hoursOf, money, localToday, addDays,
} from '../../agency/shared';

/**
 * Agencies > Bookings and Agencies > Hours. Both open the same attendance
 * modal as the rota's Agency group, so a booking is handled the same way
 * wherever the manager meets it.
 */

const agencyOptions = (agencies) => [{ value: '', label: 'All agencies' }, ...agencies.map((a) => ({ value: String(a.id), label: a.name }))];

export const BookingsTab = ({ agencies, onChanged }) => {
  const [view, setView] = useState('upcoming');
  const [agencyId, setAgencyId] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await homeAgencyService.bookings({ view, agency_id: agencyId || undefined });
      setRows(res.data || []);
      setCounts(res.meta?.counts || {});
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load bookings.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [view, agencyId]);
  useEffect(() => { load(); }, [load]);
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const columns = [
    { key: 'shift_date', header: 'Shift', sortable: true, render: (b) => <span className="text-[13px] font-semibold">{dayLabel(b.shift_date)} · {timeRange(b)}</span> },
    { key: 'worker', header: 'Staff', render: (b) => <Person name={b.worker?.name} sub={b.worker?.user_id} image={b.worker?.avatar_url} /> },
    { key: 'agency', header: 'Agency', render: (b) => b.agency?.name },
    { key: 'unit', header: 'Unit', render: (b) => b.unit || '—' },
    { key: 'rate', header: 'Charge', render: (b) => `${money(b.hourly_rate)}/h` },
    { key: 'state', header: 'Status', render: (b) => <BookingPill booking={b} /> },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <FilterTabs
          value={view}
          onChange={setView}
          tabs={[
            { value: 'upcoming', label: 'Upcoming', count: counts.upcoming },
            { value: 'attention', label: 'Needs you', count: counts.attention, alert: true },
            { value: 'completed', label: 'Worked', count: counts.completed },
            { value: 'cancelled', label: 'Cancelled', count: counts.cancelled },
          ]}
        />
        {agencies.length > 1 && <Select value={agencyId} onChange={setAgencyId} options={agencyOptions(agencies)} className="w-full sm:w-64" />}
      </div>
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        rowKey={(b) => b.id}
        onRowClick={setOpen}
        emptyIcon={CalendarCheck}
        emptyTitle="No bookings here"
        emptyText="Confirm an agency's offer under Cover requests to book someone."
      />
      <AttendanceModal booking={open} onClose={() => setOpen(null)} onChanged={() => { load(); onChanged(); }} />
    </div>
  );
};

export const HoursTab = ({ agencies, onChanged }) => {
  const [from, setFrom] = useState(addDays(localToday(), -30));
  const [to, setTo] = useState(localToday());
  const [status, setStatus] = useState('to_approve');
  const [agencyId, setAgencyId] = useState('');
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await homeAgencyService.hours({ from, to, agency_id: agencyId || undefined });
      setRows(res.data || []);
      setTotals(res.meta?.totals || {});
    } catch (err) {
      if (!quiet) toast.error(errorMessage(err, 'Could not load agency hours.'));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [from, to, agencyId]);
  useEffect(() => { load(); }, [load]);
  // A carer clocking out puts their hours here to approve straight away.
  useLiveRefresh(['agency'], () => load({ quiet: true }));

  const list = rows.filter((r) => status === 'all'
    || (status === 'missed' ? r.state === 'missed' : r.hours === status));

  const exportCsv = async () => {
    setExporting(true);
    try {
      await homeAgencyService.exportHours({ from, to, agency_id: agencyId || undefined });
    } catch (err) {
      toast.error(errorMessage(err, 'Could not export.'));
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    { key: 'shift_date', header: 'Shift', sortable: true, render: (b) => <span className="text-[13px] font-semibold">{dayLabel(b.shift_date)} · {timeRange(b)}</span> },
    { key: 'worker', header: 'Staff', render: (b) => <Person name={b.worker?.name} sub={b.agency?.name} image={b.worker?.avatar_url} /> },
    { key: 'actual', header: 'Clocked', render: (b) => (b.actual?.clock_in ? `${clockOf(b.actual.clock_in)}–${b.actual.clock_out ? clockOf(b.actual.clock_out) : '…'}` : '—') },
    { key: 'worked', header: 'Hours', render: (b) => (b.actual ? hoursOf(b.actual.worked_minutes) : '—') },
    { key: 'charge', header: 'Charge', render: (b) => money(b.charge) },
    { key: 'hours', header: 'Status', render: (b) => <HoursPill booking={b} /> },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="To approve" value={totals.to_approve || 0} tone={totals.to_approve ? 'warn' : undefined} />
        <Stat label="Missed clocking" value={totals.missed || 0} tone={totals.missed ? 'crit' : undefined} />
        <Stat label="Approved hours" value={hoursOf(totals.approved_minutes)} />
        <Stat label="Approved charge" value={money(totals.approved_charge || 0)} />
      </div>
      <div className={`${CARD} p-4 flex flex-wrap items-end justify-between gap-3`}>
        <div className="flex flex-wrap items-end gap-3">
          <div><FieldLabel>From</FieldLabel><input type="date" className={`${inputClass} w-[160px]`} value={from} max={to} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><FieldLabel>To</FieldLabel><input type="date" className={`${inputClass} w-[160px]`} value={to} min={from} onChange={(e) => setTo(e.target.value)} /></div>
          {agencies.length > 1 && (
            <div><FieldLabel>Agency</FieldLabel><Select value={agencyId} onChange={setAgencyId} options={agencyOptions(agencies)} className="w-[220px]" /></div>
          )}
        </div>
        <Button variant="secondary" leftIcon={<Download className="w-4 h-4" />} loading={exporting} onClick={exportCsv}>Export approved (CSV)</Button>
      </div>
      <FilterTabs
        value={status}
        onChange={setStatus}
        tabs={[
          { value: 'to_approve', label: 'To approve', count: rows.filter((r) => r.hours === 'to_approve').length, alert: true },
          { value: 'missed', label: 'Missed clocking', count: rows.filter((r) => r.state === 'missed').length },
          { value: 'approved', label: 'Approved', count: rows.filter((r) => r.hours === 'approved').length },
          { value: 'all', label: 'All', count: rows.length },
        ]}
      />
      <DataTable
        columns={columns}
        data={list}
        loading={loading}
        rowKey={(b) => b.id}
        onRowClick={setOpen}
        emptyIcon={Timer}
        emptyTitle="Nothing here"
        emptyText="Agency hours appear once their staff clock in with your QR code."
      />
      <AttendanceModal booking={open} onClose={() => setOpen(null)} onChanged={() => { load(); onChanged(); }} />
    </div>
  );
};
